/**
 * /promos — list of slot overrides (ข่าวประชาสัมพันธ์).
 *
 * Shows every override grouped by page; each row has a derived status chip
 * (upcoming/active/expired), source label (post title or inline title), and
 * an orphan banner when the override's slot_id no longer matches the page's
 * current layout (admin deleted/re-added that slot).
 */

import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Megaphone, Plus, Trash2 } from 'lucide-react';
import type { ListSlotOverridesQuery, SlotOverrideSummary } from '@news-cms/shared';
import { Button, buttonVariants } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import { slotOverridesListQueryOptions, useDeleteSlotOverride } from '@/lib/slot-overrides';

interface PromosSearch {
  status?: 'all' | 'active' | 'upcoming' | 'expired';
  pageSlug?: string;
}

export const Route = createFileRoute('/_authenticated/promos/')({
  validateSearch: (search: Record<string, unknown>): PromosSearch => ({
    status:
      search.status === 'active' ||
      search.status === 'upcoming' ||
      search.status === 'expired' ||
      search.status === 'all'
        ? search.status
        : undefined,
    pageSlug: typeof search.pageSlug === 'string' ? search.pageSlug : undefined,
  }),
  component: PromosListPage,
});

const STATUS_LABEL: Record<SlotOverrideSummary['status'], string> = {
  upcoming: 'รอเริ่ม',
  active: 'กำลังแสดง',
  expired: 'หมดอายุ',
};

const STATUS_CLASS: Record<SlotOverrideSummary['status'], string> = {
  upcoming: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  expired: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

function PromosListPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const canManage = hasPermission(user, 'promos.manage');

  const filter: Partial<ListSlotOverridesQuery> = {
    status: search.status ?? 'all',
    pageSlug: search.pageSlug,
  };
  const query = useQuery(slotOverridesListQueryOptions(filter));
  const deleteMutation = useDeleteSlotOverride();
  const [pendingDelete, setPendingDelete] = useState<SlotOverrideSummary | null>(null);

  const items = query.data?.items ?? [];

  if (!canManage) {
    return (
      <div className="p-8 text-center text-sm text-slate-500">
        ไม่มีสิทธิ์เข้าถึง — ต้องมี <code>promos.manage</code>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              <Megaphone className="h-6 w-6 text-amber-500" /> ข่าวประชาสัมพันธ์
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              PR ที่ทับ slot บนหน้า <code>/pages</code> ตามช่วงเวลา — หมดเวลาแล้วจะหายเอง slot
              กลับไปแสดงเนื้อหาปกติ
            </p>
          </div>
          <Link to="/promos/new" className={buttonVariants({ variant: 'default' })}>
            <Plus className="mr-1 h-4 w-4" /> สร้าง PR
          </Link>
        </div>

        {/* Status filter */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">สถานะ:</span>
          {(['all', 'active', 'upcoming', 'expired'] as const).map((s) => {
            const active = (search.status ?? 'all') === s;
            const label =
              s === 'all'
                ? 'ทั้งหมด'
                : s === 'active'
                  ? 'กำลังแสดง'
                  : s === 'upcoming'
                    ? 'รอเริ่ม'
                    : 'หมดอายุ';
            return (
              <button
                key={s}
                type="button"
                onClick={() =>
                  void navigate({ search: { ...search, status: s === 'all' ? undefined : s } })
                }
                className={`rounded-full border px-2.5 py-0.5 transition ${
                  active
                    ? 'border-amber-400 bg-amber-50 text-amber-700 dark:border-amber-500 dark:bg-amber-950/30 dark:text-amber-300'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Orphan summary banner */}
        {items.some((i) => i.isOrphan) && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
            มี {items.filter((i) => i.isOrphan).length} PR ที่ slot ถูกลบจาก layout ปัจจุบัน —
            ลบหรือชี้ slot ใหม่
          </div>
        )}

        {/* Table */}
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2 font-medium">หัวข้อ / ชนิด</th>
                <th className="px-4 py-2 font-medium">หน้า / Slot</th>
                <th className="px-4 py-2 font-medium">ช่วงเวลา</th>
                <th className="px-4 py-2 font-medium">สถานะ</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {query.isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                    กำลังโหลด…
                  </td>
                </tr>
              )}
              {!query.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                    {search.status && search.status !== 'all'
                      ? `ไม่มี PR สถานะ "${STATUS_LABEL[search.status]}"`
                      : 'ยังไม่มี PR — กด "สร้าง PR" เพื่อเพิ่ม'}
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr
                  key={item.id}
                  className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                    item.isOrphan ? 'bg-red-50/40 dark:bg-red-950/10' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 dark:text-slate-50">
                      {item.sourceLabel || '(ไม่มีหัวข้อ)'}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-400">
                      {item.source.kind === 'article' ? '📰 บทความ' : '🔗 inline'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="text-slate-700 dark:text-slate-200">{item.pageName}</div>
                    <code className="text-slate-400">/{item.pageSlug}</code>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1">
                      <code className="rounded bg-slate-100 px-1 text-[10px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                        {item.slotId}
                      </code>
                      {item.targetKind === 'carousel-slide' && item.slideIndex !== null && (
                        <span className="rounded bg-indigo-100 px-1 text-[10px] text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                          slide #{item.slideIndex}
                        </span>
                      )}
                      {item.isOrphan && (
                        <span className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1 text-[10px] text-red-700 dark:bg-red-900/50 dark:text-red-300">
                          <AlertTriangle className="h-2.5 w-2.5" /> orphan
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    <div>{formatDate(item.startsAt)}</div>
                    <div className="text-slate-400">→ {formatDate(item.endsAt)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${STATUS_CLASS[item.status]}`}
                    >
                      {STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to="/promos/$id/edit"
                        params={{ id: item.id }}
                        className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                        title="แก้ไข"
                      >
                        แก้ไข
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingDelete(item)}
                        title="ลบ"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-center text-xs text-slate-400">
          PR ที่ active อยู่จะแสดงทับ slot โดยอัตโนมัติบน public site ทันที — หลัง endsAt slot
          กลับเป็นเนื้อหาเดิม ไม่ต้องลบ row
        </p>
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="ลบ PR?"
        description={
          pendingDelete
            ? `จะลบ PR "${pendingDelete.sourceLabel}" จาก ${pendingDelete.pageName}/${pendingDelete.slotId}`
            : ''
        }
        confirmLabel="ลบ"
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteMutation.mutate(pendingDelete.id, {
            onSuccess: () => setPendingDelete(null),
          });
        }}
      />
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('th-TH', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}
